import classNames from 'classnames';
import Image from 'next/image';
import React, { VFC, useState } from 'react';
import { Controlled as ControlledZoom } from 'react-medium-image-zoom';

import 'react-medium-image-zoom/dist/styles.css';

import useWindowDimensions from '@/hooks/useWindowDimensions';
import { imgixOptimizedLoader } from '@/utils/imgixLoaders';
import { getBlurImageURL } from '@/utils/imgixUtils';
import isGif from '@/utils/isGif';

import styles from './ImageExhibition.module.scss';
import ExpansionIcon from './icons/expansion-icon.svg';

type Props = {
  imageURL: string;
  width: number;
  height: number;
};

const ImageExhibition: VFC<Props> = ({
  imageURL,
  width: naturalImageWidth,
  height: naturalImageHeight,
}: Props) => {
  const [isZoomed, setIsZoomed] = useState(false);

  const { width: windowWidth } = useWindowDimensions();
  const isSmallScreen = windowWidth < 400;

  // Render size settings
  const IMAGE_MAX_HEIGHT = 500;
  const CONTAINER_PADDING_X = 25;
  const CONTAINER_PADDING_Y = isSmallScreen ? 80 : 100;

  // Image rendering
  const imageWidth = Math.min(
    Math.floor(naturalImageWidth * (IMAGE_MAX_HEIGHT / naturalImageHeight)),
    windowWidth - CONTAINER_PADDING_X * 2
  );
  const imageHeight = Math.min(
    IMAGE_MAX_HEIGHT,
    Math.floor(naturalImageHeight * (imageWidth / naturalImageWidth))
  );

  const containerHeight = Math.min(imageHeight + CONTAINER_PADDING_Y * 2);

  return (
    <div className={styles.imageExhibition} style={{ height: containerHeight }}>
      <ControlledZoom
        isZoomed={isZoomed}
        onZoomChange={(_isZoomed) => setIsZoomed(_isZoomed)}
      >
        <div
          className={styles.imageContainer}
          style={{
            position: 'relative',
            width: imageWidth ?? 1024,
            height: imageHeight ?? 576,
          }}
        >
          <Image
            src={imageURL}
            loader={!isGif(imageURL) ? imgixOptimizedLoader : undefined}
            layout="fill"
            objectFit="contain"
            placeholder="blur"
            blurDataURL={getBlurImageURL(imageURL)}
          />
        </div>
      </ControlledZoom>

      <button
        type="button"
        onClick={() => setIsZoomed((prev) => !prev)}
        className={classNames(styles.fullScreenButton, {
          [styles.fullScreenButtonActive]: isZoomed,
        })}
      >
        <ExpansionIcon />
      </button>
    </div>
  );
};

export default ImageExhibition;
